import { DEFAULT_PLANNED_SAMPLE_SIZE } from "@digital-twins/shared-types";
import type { AudienceSelectionRef } from "@digital-twins/shared-types";
import type { ReviewPin } from "../state/creationState";
import {
  queueReviewDraftWrite,
  writeAtReviewPin,
  type ReviewDraftWrite,
} from "./reviewDraftWrite";

const PENDING_POPULATION_ID = "population_pending_selection";

function storedPlannedSampleSize(audience: AudienceSelectionRef): number | null {
  return typeof audience.plannedSampleSize === "number"
    ? audience.plannedSampleSize
    : null;
}

function hasRealAudience(
  audience: AudienceSelectionRef | undefined,
): audience is AudienceSelectionRef {
  return (
    typeof audience?.populationId === "string" &&
    audience.populationId.length > 0 &&
    audience.populationId !== PENDING_POPULATION_ID
  );
}

/**
 * Store the planned sample size on the draft, beside the audience pool.
 *
 * The write is Review's compare-and-swap (`writeAtReviewPin`), with the
 * reviewed audience as its base: only `plannedSampleSize` changes, so the
 * revision it makes is still the survey and audience Review shows, and Review
 * re-pins to it. The pool stays on `estimatedRespondentCount`. A draft with
 * no stored size on the product default is left untouched: resume already
 * restores that default, and a write would revise the questionnaire for
 * nothing.
 *
 * It queues on the draft's one Review write lane, so a newer size replaces an
 * older one that has not sent yet.
 */
export function persistPlannedSampleSize(
  draftId: string,
  sampleSize: number,
  pin: { current: ReviewPin },
): Promise<ReviewDraftWrite> {
  return queueReviewDraftWrite(draftId, "sampleSize", sampleSize, async () => {
    const revision = pin.current.revision;
    const audience = pin.current.audience ?? undefined;
    if (revision === null || !hasRealAudience(audience)) {
      return { kind: "no_audience" };
    }

    const reviewedSampleSize = storedPlannedSampleSize(audience);
    if (
      reviewedSampleSize === sampleSize ||
      (reviewedSampleSize == null && sampleSize === DEFAULT_PLANNED_SAMPLE_SIZE)
    ) {
      return { kind: "unchanged" };
    }

    return writeAtReviewPin(draftId, pin, revision, {
      audience: { ...audience, plannedSampleSize: sampleSize },
    });
  });
}
